/**
 * The rationing a proposal line met, as the run recorded it (bata-platform issue 603, ADR 6074,
 * `docs/adr/6021`). Pure: no React, no network.
 *
 * A line cut under the DC's stock or the demand rank could not answer the planner's next two
 * questions: which doors were served first, and how much of the size the DC held for how many
 * asks. The run now records three groups on every line, each beside its own absent reason:
 *
 *   • the door's place in the engine's order for the option's DC pool (`rationing_rank` of
 *     `rationing_doors`, 1 served first);
 *   • the DC's pool of the line's size: before any door was served, what every door of the option
 *     asked for, and what the solved plan left (`dc_pool_units`, `dc_pool_requested_units`,
 *     `dc_pool_after_units`) — measured on the solved plan, before the weekly move cap and the
 *     shipment minimum, which defer whole drops and never shorten a line;
 *   • the size's share of the door's size curve (`size_curve_share`).
 *
 * Nothing is derived here: each figure is the platform's, and a group with any figure missing is
 * an absence with the platform's reason in words, never a nought. A line from a platform older than
 * ADR 6074 carries no field at all, which reads as «not recorded by this run».
 */

import type { ProposalSummary } from '@/api/generated';

/** A recorded group, or why the run recorded none. */
export type Recorded<T> =
  | { readonly kind: 'value'; readonly value: T }
  | { readonly kind: 'absent'; readonly reasonKey: string; readonly reasonValues?: Readonly<Record<string, string>> };

/** The platform's absent reasons (ADR 6074), each a catalogue key — written out for the census. */
export const RATIONING_REASON_KEY: Readonly<Record<string, string>> = {
  horizon_not_recorded_by_run: 'inseason.rationing.reason.horizonNotRecorded',
  rationing_not_recorded_by_run: 'inseason.rationing.reason.notRecordedByRun',
  door_not_ranked: 'inseason.rationing.reason.doorNotRanked',
  dc_pool_by_size_not_measured: 'inseason.rationing.reason.dcPoolNotMeasured',
  store_total_line: 'inseason.rationing.reason.storeTotalLine',
  size_curve_not_recorded: 'inseason.rationing.reason.sizeCurveNotRecorded',
};

/** A reason this build has no words for: shown as the platform's code, never as a plausible guess. */
export const UNRECOGNISED_RATIONING_REASON_KEY = 'inseason.rationing.reason.unrecognised';

/** The reason a run that sent no reason at all — a platform older than ADR 6074 — is absent for. */
const NOT_RECORDED = 'rationing_not_recorded_by_run';

export function absentFor(code: string | null | undefined): Recorded<never> {
  const reason = code ?? NOT_RECORDED;
  const key = RATIONING_REASON_KEY[reason];
  return key === undefined
    ? { kind: 'absent', reasonKey: UNRECOGNISED_RATIONING_REASON_KEY, reasonValues: { code: reason } }
    : { kind: 'absent', reasonKey: key };
}

type Line = Pick<ProposalSummary,
  | 'rationing_rank' | 'rationing_doors' | 'rationing_rank_absent_reason'
  | 'dc_pool_units' | 'dc_pool_requested_units' | 'dc_pool_after_units' | 'dc_pool_absent_reason'
  | 'size_curve_share' | 'size_curve_share_absent_reason'>;

/** The door's place in the engine's order for the option's DC pool, and how many doors it ranks. */
export function rationingRank(line: Line): Recorded<{ readonly rank: number; readonly doors: number }> {
  const rank = line.rationing_rank ?? null;
  const doors = line.rationing_doors ?? null;
  return rank === null || doors === null ? absentFor(line.rationing_rank_absent_reason) : { kind: 'value', value: { rank, doors } };
}

/** The DC's pool of the line's size: before, asked by every door of the option, and after the solve. */
export function dcPool(line: Line): Recorded<{ readonly before: number; readonly asked: number; readonly after: number }> {
  const before = line.dc_pool_units ?? null;
  const asked = line.dc_pool_requested_units ?? null;
  const after = line.dc_pool_after_units ?? null;
  return before === null || asked === null || after === null
    ? absentFor(line.dc_pool_absent_reason)
    : { kind: 'value', value: { before, asked, after } };
}

/** The size's share of the door's size curve, 0–1: the split the engine applied. */
export function sizeCurveShare(line: Line): Recorded<number> {
  const share = line.size_curve_share ?? null;
  return share === null ? absentFor(line.size_curve_share_absent_reason) : { kind: 'value', value: share };
}

/**
 * The row's place in the DC's order for ranking a list by it (client request, 2026-10-14): the
 * best — lowest — rank any of its lines recorded, since the engine ranks a door per size and the
 * row is served first where its first size is. `null` where no line recorded one: an absence,
 * which a ranking puts last, never a rank.
 */
export function bestRationingRank(lines: readonly Pick<ProposalSummary, 'rationing_rank'>[]): number | null {
  let best: number | null = null;
  for (const line of lines) {
    const rank = line.rationing_rank ?? null;
    if (rank !== null && (best === null || rank < best)) best = rank;
  }
  return best;
}

/**
 * The one answer the lines share — the same rank of the same doors, or the same absence — or `null`
 * when they differ, and the per-size table is where each size's own answer is read.
 */
export function sharedRank(lines: readonly Line[]): Recorded<{ readonly rank: number; readonly doors: number }> | null {
  const ranks = lines.map(rationingRank);
  const [head] = ranks;
  if (head === undefined) return null;
  const same = ranks.every((rank) => (rank.kind === 'value' && head.kind === 'value'
    ? rank.value.rank === head.value.rank && rank.value.doors === head.value.doors
    : rank.kind === 'absent' && head.kind === 'absent' && rank.reasonKey === head.reasonKey));
  return same ? head : null;
}
